// ! ===============================  COOKIE HELPERS =================================

import { EXPIRES, MAX_AGE, PATH } from "../constants/storage";

/**
 * Sets a cookie with the given name, value, and expiration days.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value of the cookie.
 * @param {number} [days] - The number of days until the cookie expires. Optional.
 */
export const setCookie = (name, value, days) => {
  const expires = days
    ? `; ${EXPIRES}=${new Date(Date.now() + days * 864e5).toUTCString()}`
    : "";
  document.cookie = `${name}=${value}${expires}; ${PATH}=/`;
};

/**
 * Gets the value of the cookie with the given name.
 *
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string|null} - The value of the cookie, or null if the cookie does not exist.
 */
export const getCookie = (name) => {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
};

/**
 * Removes the cookie with the given name.
 *
 * @param {string} name - The name of the cookie to remove.
 */
export const removeCookie = (name) => {
  document.cookie = `${name}=; ${MAX_AGE}=-99999999; ${PATH}=/`;
};

/**
 * Updates the cookie with the given name and new value.
 *
 * @param {string} name - The name of the cookie to update.
 * @param {string} value - The new value of the cookie.
 * @param {number} [days] - The number of days until the cookie expires. Optional.
 */
export const updateCookie = (name, value, days) => {
  setCookie(name, value, days);
};

// ! ===============================  LOCAL STORAGE HELPERS =================================

/**
 * Sets an item in localStorage.
 *
 * @param {string} key - The key of the item to store.
 * @param {string} value - The value of the item to store.
 */
export const setLocalStorage = (key, value) => {
  localStorage.setItem(key, value);
};

/**
 * Gets an item from localStorage.
 *
 * @param {string} key - The key of the item to retrieve.
 * @returns {string|null} - The value of the item, or null if the item does not exist.
 */
export const getLocalStorage = (key) => {
  return localStorage.getItem(key);
};

/**
 * Removes an item from localStorage.
 *
 * @param {string} key - The key of the item to remove.
 */
export const removeLocalStorage = (key) => {
  localStorage.removeItem(key);
};

/**
 * Updates an item in localStorage.
 *
 * @param {string} key - The key of the item to update.
 * @param {string} value - The new value of the item.
 */
export const updateLocalStorage = (key, value) => {
  setLocalStorage(key, value);
};

// ! ===============================  SESSION STORAGE HELPERS =================================

/**
 * Sets an item in sessionStorage.
 *
 * @param {string} key - The key of the item to store.
 * @param {string} value - The value of the item to store.
 */
export const setSessionStorage = (key, value) => {
  sessionStorage.setItem(key, value);
};

/**
 * Gets an item from sessionStorage.
 *
 * @param {string} key - The key of the item to retrieve.
 * @returns {string|null} - The value of the item, or null if the item does not exist.
 */
export const getSessionStorage = (key) => {
  return sessionStorage.getItem(key);
};

/**
 * Removes an item from sessionStorage.
 *
 * @param {string} key - The key of the item to remove.
 */
export const removeSessionStorage = (key) => {
  sessionStorage.removeItem(key);
};

/**
 * Updates an item in sessionStorage.
 *
 * @param {string} key - The key of the item to update.
 * @param {string} value - The new value of the item.
 */
export const updateSessionStorage = (key, value) => {
  setSessionStorage(key, value);
};
